import ApplicationModel, { attr } from '../ApplicationModel';
import DateRangeFilter, {
  type DateRangeFilterValue,
} from '../filters/DateRangeFilter';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import ModeFilter, { type ModeFilterValue } from '../filters/ModeFilter';
import TeamVelocityTeamFilter from '../filters/TeamVelocityTeamFilter';
import type { TeamVelocityTeamFilterValue } from '../filters/TeamVelocityTeamFilter';
import { model } from '../modelBuilder';
import Query from '../Query';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import type { BookmarksTeamVelocityChartAttributes } from './generated/BookmarksTeamVelocityChartAttributes';
import type { BookmarksTeamVelocityChartDataRow } from './generated/types';

type Filters = {
  dateRange: DateRangeFilter;
  estimationType: EstimateTypeFilter;
  mode: ModeFilter;
  team: TeamVelocityTeamFilter;
};

type FilterValues = {
  dateRange?: DateRangeFilterValue;
  estimationType?: EstimateTypeFilterValue[];
  mode?: ModeFilterValue[];
  team?: TeamVelocityTeamFilterValue[];
};

export type FiltersScope = keyof Filters;

interface BookmarksTeamVelocityChart
  extends BookmarksTeamVelocityChartAttributes,
    HasBookmarkFilters<Filters> {
  filters: FilterValues;
  filterObjects: Filters;
}

@model
@HasBookmarkFilters
class BookmarksTeamVelocityChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksTeamVelocityChart' = 'BookmarksTeamVelocityChart';
  get typename(): 'BookmarksTeamVelocityChart' {
    return 'BookmarksTeamVelocityChart';
  }

  static internalTypename: 'Bookmarks::TeamVelocityChart' =
    'Bookmarks::TeamVelocityChart';
  get internalTypename(): 'Bookmarks::TeamVelocityChart' {
    return 'Bookmarks::TeamVelocityChart';
  }

  static bookmarkName = 'Bookmarks::TeamVelocityChart';

  static inputType = 'TeamVelocityChartAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    chartData: attr.hasMany({
      args: { dateFrom: 'ISO8601DateTime', dateTo: 'ISO8601DateTime' },
      readable: true,
    }),
  };
  // End auto-generated

  initialize() {
    this.local = { filterObjects: {} };
    this.filters = this.filters || {};
  }

  async createFilters(): Promise<Filters> {
    const defaultCapacityUnits = this.project?.defaultCapacityUnits || 'POINTS';

    const estimateTypeFilter = new EstimateTypeFilter({
      initialItems: this.filters.estimationType || ['originalEstimate'],
      capacityUnits: defaultCapacityUnits,
    });

    const modeFilter = new ModeFilter({
      initialItems: this.filters.mode || ['velocity'],
    });

    const teamFilter = new TeamVelocityTeamFilter({
      initialItems: this.filters.team || [],
    });

    return {
      dateRange: new DateRangeFilter(
        this.filters.dateRange ?? { start: '', end: '' },
        false
      ),
      estimationType: estimateTypeFilter,
      mode: modeFilter,
      team: teamFilter,
    };
  }

  async fetchChartData(args: {
    dateFrom?: string | Date;
    dateTo?: string | Date;
  }): Promise<BookmarksTeamVelocityChartDataRow[]> {
    const params = Object.fromEntries(
      Object.entries(args).filter(([, v]) => !!v)
    );

    const scope = BookmarksTeamVelocityChart.merge({
      chartData: new Query()
        .select([
          'iterationId',
          'projectId',
          'teamName',
          'iterationName',
          'iterationReferenceNum',
          'iterationStarted',
          'teamMemberCount',
          // 'workDone',
          'originalEstimate',
          'completedCount',
          'committedOriginalEstimate',
          'committedCount',
        ])
        .argument(params),
    });

    const data = await scope.find(this.id);

    return data?.chartData;
  }
}

export default BookmarksTeamVelocityChart;
